import DisabledMask from 'components/DisabledMask';
import type { ScrollbarsRefType } from 'components/scrollbars';
import { Scrollbars } from 'components/scrollbars';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { positionValues } from 'react-custom-scrollbars-2';
import type { toggleHourTimestampFromMemory } from 'redux/modules/hours.actions';
import type {
  deleteEntryIds,
  markEntryIdsAsNotSpam,
  markEntryIdsAsSpam,
  predictEntryIdsToProjectId,
  setFilter,
  toggleDisplayRuleForMode,
  toggleExpandEntryId,
  toggleExpandGroupRef,
} from 'redux/modules/timeline.actions';
import type { Filter } from 'redux/modules/timeline.types';
import type { MemorySidebarUIProps } from 'redux/selectors/timeline.memories';
import MemoryGroupUpgradeState from 'routes/supercalendar/components/calendar/memory/MemoryGroupUpgradeState';
import type { MappedAction } from 'types/redux';
import type {
  ExtractTimelineMetaFromEntries,
  MemoriesGroupByStatus,
  MemoriesGroupedByStatus,
  MemoryGroup as MemoryGroupType,
} from 'types/timeline';
import { MemoryGroup } from './MemoryGroup';
import { MemoryGroupEmptyState } from './MemoryGroupEmptyState';
import { MemoryGroupPlaceholder } from './MemoryGroupPlaceholder';
import styles from './MemoryGroups.module.scss';

interface MemoryGroupsProps {
  toggleSelectEntryIds: (entry_ids: Array<number>) => void;
  toggleHourTimestampFromMemory: MappedAction<typeof toggleHourTimestampFromMemory>;
  toggleExpandGroupRef: MappedAction<typeof toggleExpandGroupRef>;
  toggleExpandEntryId: MappedAction<typeof toggleExpandEntryId>;
  toggleDisplayRuleForMode: MappedAction<typeof toggleDisplayRuleForMode>;
  markEntryIdsAsSpam: MappedAction<typeof markEntryIdsAsSpam>;
  markEntryIdsAsNotSpam: MappedAction<typeof markEntryIdsAsNotSpam>;
  predictEntryIdsToProjectId: MappedAction<typeof predictEntryIdsToProjectId>;
  deleteEntryIds: MappedAction<typeof deleteEntryIds>;
  groups: MemoriesGroupedByStatus | null | undefined;
  meta: ExtractTimelineMetaFromEntries;
  ui: MemorySidebarUIProps;
  disabled: boolean;
  filter: Filter;
  setFilter: MappedAction<typeof setFilter>;
  hasExperimentalStyle?: boolean;
  setScrollPosition?: (scrollPosition: number) => void;
  initialScrollPosition: number;
}

const MemoryGroups: React.FC<MemoryGroupsProps> = props => {
  const {
    groups,
    ui,
    disabled = false,
    hasExperimentalStyle,
    toggleSelectEntryIds,
    setScrollPosition,
    initialScrollPosition,
  } = props;
  const { emptyStateType, isLoading, showPlaceholderMemories } = ui;
  const scrollbarRef = useRef<ScrollbarsRefType | null>(null);
  const [scrollHeight, setScrollHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    scrollbarRef.current?.scrollTop?.(initialScrollPosition);
    setScrollPosition?.(initialScrollPosition);
  }, [initialScrollPosition, setScrollPosition]);

  const handleToggleSelectEntryIds = useCallback(
    (entry_ids: Array<number>) => {
      toggleSelectEntryIds(entry_ids);
    },
    [toggleSelectEntryIds]
  );

  const handleCheckScrollHeight = useCallback(
    (values: positionValues) => {
      if (values.scrollHeight !== scrollHeight) {
        setScrollHeight(values.scrollHeight);
      }
      setScrollPosition?.(values.scrollTop);
    },
    [scrollHeight, setScrollPosition]
  );

  const renderGroup = (group: MemoryGroupType, section: MemoriesGroupByStatus) => {
    const key = `${section.key}-${group.ref}`;
    return (
      <MemoryGroup
        toggleHourTimestampFromMemory={props.toggleHourTimestampFromMemory}
        operatorMode={props.ui.operatorMode}
        key={key}
        showDebugInfo={props.ui.showDebugInfo}
        groupSection={section.key}
        toggleExpandGroupRef={props.toggleExpandGroupRef}
        toggleExpandEntryId={props.toggleExpandEntryId}
        toggleSelectEntryIds={handleToggleSelectEntryIds}
        markEntryIdsAsSpam={props.markEntryIdsAsSpam}
        markEntryIdsAsNotSpam={props.markEntryIdsAsNotSpam}
        predictEntryIdsToProjectId={props.predictEntryIdsToProjectId}
        deleteEntryIds={props.deleteEntryIds}
        group={group}
        hasEnabledTic={props.ui.hasEnabledTic}
      />
    );
  };

  const renderGroups = (section: MemoriesGroupByStatus) => {
    if (props.ui.showUpgradeState) {
      return <MemoryGroupUpgradeState />;
    }

    // Abort render, or show empty text / placeholder UI (if set)
    if (!section.groups.length && !props.ui.isLoading) {
      if (!section.emptyText) {
        return <MemoryGroupEmptyState ui={props.ui} />;
      }
      return (
        <div key={`${section.key}-empty`} className={styles.emptyGroup}>
          {section.emptyText}
        </div>
      );
    }

    return section.groups.map(group => renderGroup(group, section));
  };

  if (!groups) {
    return null;
  }

  const spacer = <div style={{ height: 10 }} />;

  return (
    <Scrollbars
      ref={scrollbarRef}
      autoHide={showPlaceholderMemories}
      className={styles.container}
      onScrollFrame={handleCheckScrollHeight}
      data-testid="memory_inbox"
    >
      <DisabledMask active={disabled}>
        {showPlaceholderMemories && (
          <MemoryGroupPlaceholder isLoading={isLoading} emptyType={emptyStateType} numItems={35} />
        )}
        {spacer}
        {renderGroups(hasExperimentalStyle ? groups.all : groups.other)}
        {spacer}
      </DisabledMask>
    </Scrollbars>
  );
};

export { MemoryGroups };
export default MemoryGroups;
